import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { useScrollSpy } from 'utils/hooks/useScrollSpy';

import { TOP_SCROLL_OFFSET } from '../NavigationAnchor';

type StickyContextType = {
  activeAnchor: string | undefined;
  anchorsHasMap: Record<string, number | string>;
  isNavVisible: boolean;
  setAnchorsHasMap: Dispatch<SetStateAction<Record<string, number | string>>>;
};

export const cartContextDefaultValues: StickyContextType = {
  activeAnchor: undefined,
  anchorsHasMap: {},
  isNavVisible: false,
  setAnchorsHasMap: () => {},
};

const StickyContext = createContext<StickyContextType>(
  cartContextDefaultValues,
);
export const useStickyContext = (): StickyContextType => {
  const contextValue = useContext(StickyContext);

  if (contextValue === undefined) {
    throw new Error(
      'Expected a StickyContext in the React tree to set the context value',
    );
  }

  return contextValue;
};

export type StickyProviderProps = {
  children: React.ReactNode;
  mainSectionId?: string;
  anchorsSectionsIds?: string[];
};

const isAboveScreen = (value: number, _floor: number, ceil: number) =>
  value >= ceil;

// The 1px is needed to set the active anchor when the main section is above the screen
function StickyContextProvider(props: StickyProviderProps): JSX.Element {
  const [anchorsHasMap, setAnchorsHasMap] = useState<
    Record<string, number | string>
  >({} as Record<string, number | string>);
  const { mainSectionId, anchorsSectionsIds = [] } = props;
  const lastAnchorSection = [anchorsSectionsIds[anchorsSectionsIds.length - 1]];
  const mainSection = mainSectionId ? [mainSectionId] : [];

  const IsMainSectionAboveScreen = Boolean(
    useScrollSpy(mainSection, TOP_SCROLL_OFFSET + 1, isAboveScreen),
  );
  const activeAnchor = useScrollSpy(anchorsSectionsIds, TOP_SCROLL_OFFSET + 1);

  const IsLastAnchorAboveScreen = Boolean(
    useScrollSpy(lastAnchorSection, TOP_SCROLL_OFFSET + 1, isAboveScreen),
  );
  return (
    <StickyContext.Provider
      value={{
        setAnchorsHasMap,
        activeAnchor: activeAnchor,
        anchorsHasMap,
        isNavVisible: IsMainSectionAboveScreen && !IsLastAnchorAboveScreen,
      }}
    >
      {props.children}
    </StickyContext.Provider>
  );
}

export default StickyContextProvider;
